import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from "axios";
import './style.css';
import { ReactMic } from 'react-mic';
import NoraZoom from "../../images/nora-zoom.png";
import VideoTimer from "../../images/nora-timer.png";
import { Oval } from 'react-loader-spinner'; 
import ErrorIcon from "../../images/error-icon.png";
import InterviewCard1 from "../../images/interviewCard1.png";
import InterviewCard2 from "../../images/interviewCard2.png";
import InterviewCard3 from "../../images/interviewCard3.png";
import InterviewCard4 from "../../images/interviewCard4.png";
import InterviewCard5 from "../../images/interviewCard5.png";
import InterviewCard6 from "../../images/InterviewCard6.png";
import NoraAILogo from "../../images/noraAILogo.png";
import * as Sentry from '@sentry/react';
import InterviewCompletedScreen from '../../components/InterviewCompletedScreen';
import { useMediaQuery } from '@mui/material';
import { franc } from 'franc';
import ExclamationIcon from "../../images/exclamationIcon2.png";
import DailyIframe from '@daily-co/daily-js';
import VideoRecorderIcon from '../../images/video-recorder.png';
import AudioRecorderIcon from '../../images/microphone-01.png';

function CandidateInterviewScreen() {
  const { candidateToInterviewId } = useParams();
  const [candidateInfo, setCandidateInfo] = useState(null);
  const [started, setStarted] = useState(false);
  const [testing, setTesting] = useState(false);
  const [finished, setFinished] = useState(false);
  const [history, setHistory] = useState([]);
  const [record, setRecord] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [time, setTime] = useState(0);
  const [audioPermissionGranted, setAudioPermissionGranted] = useState(false);
  const [videoPermissionGranted, setVideoPermissionGranted] = useState(false);
  const [previewStream, setPreviewStream] = useState(null);
  const [permissionError, setPermissionError] = useState('');
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [permissionsBrowserTab, setPermissionsBrowserTab] = useState('chrome');
  const [availableVideoDevices, setAvailableVideoDevices] = useState([]);
  const [availableAudioDevices, setAvailableAudioDevices] = useState([]);
  const [selectedVideoDeviceId, setSelectedVideoDeviceId] = useState('');
  const [selectedAudioDeviceId, setSelectedAudioDeviceId] = useState('');
  const [audioLevel, setAudioLevel] = useState(0);
  const videoRef = useRef(null);
  const messageEndRef = useRef(null);
  const audioAnalyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isNoraSpeaking, setIsNoraSpeaking] = useState(false);
  const [isInterviewDone, setInterviewDone] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [hasError, setHasError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const [cheatingCounter, setCheatingCounter] = useState(0);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const noraAudioRef = useRef(null);
  const noraSourceRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioDestinationRef = useRef(null);
  const dailyAudioInputStreamRef = useRef(null);
  const dailyMicSourceRef = useRef(null);
  const [webAudioFailed, setWebAudioFailed] = useState(false);
  const [cheatingIncidents, setCheatingIncidents] = useState([]);
  const cheatingIncidentsRef = useRef([]);
  const interviewStartTimeRef = useRef(null);
  const realtimeTranscriberRef = useRef(null);
  const accumulatedTranscriptRef = useRef('');
  const [showMobileWarningModal, setShowMobileWarningModal] = useState(false);
  const dailyAudioListenerAttached = useRef(false);
  const dailyListenersAttached = useRef(false);
  const responseLatenciesRef = useRef([]);
  const noraFinishedSpeakingTimeRef = useRef(null);
  const questionTimestampsRef = useRef([]);

  const [verifying, setVerifying] = useState(false);           // Are we in verification step?
  const [verificationStatus, setVerificationStatus] = useState('none'); // none, pending, approved, declined
  const [veriffSessionUrl, setVeriffSessionUrl] = useState('');
  const [veriffError, setVeriffError] = useState('');

  // Create a Veriff session and get the redirect URL
  const createVeriffSession = async () => {
    try {
      setVeriffError('');
      
      // Get candidate name from candidateInfo (already loaded by this point)
      const fullName = candidateInfo?.candidateToInterview?.name || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/Chat/createVeriffSession`,
        {
          candidateToInterviewId: candidateToInterviewId,
          firstName: firstName,
          lastName: lastName,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { sessionUrl, sessionId } = response.data;
      setVeriffSessionUrl(sessionUrl);
      return { sessionUrl, sessionId };
    } catch (error) {
      console.error('Failed to create Veriff session:', error);
      setVeriffError('Failed to start identity verification. Please try again.');
      return null;
    }
  };

  // Poll verification status (after candidate returns from Veriff)
  const pollVerificationStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/Chat/veriffStatus/${candidateToInterviewId}`
      );
      
      const { status } = response.data;
      setVerificationStatus(status);
      return status;
    } catch (error) {
      console.error('Failed to check verification status:', error);
      return 'none';
    }
  };

  // Start verification flow
  const startVerification = async () => {
    setVerifying(true);
    setVerificationStatus('pending');
    
    const result = await createVeriffSession();
    if (result?.sessionUrl) {
      // Open Veriff in a new tab (so we don't lose the interview page)
      window.open(result.sessionUrl, '_blank');
      
      // Start polling for the result
      const pollInterval = setInterval(async () => {
        const status = await pollVerificationStatus();
        if (status === 'approved' || status === 'declined' || status === 'expired') {
          clearInterval(pollInterval);
          
          if (status === 'approved') {
            // Auto-proceed to audio/video check
            setVerifying(false);
            setTesting(true);
            try {
              await checkPermissions();
              if (!callObjectRef.current) {
                callObjectRef.current = DailyIframe.createCallObject();
                dailyListenersAttached.current = false;
              }
            } catch (error) {
              console.error('Error checking permissions after verification:', error);
            }
          }
        }
      }, 3000); // Poll every 3 seconds

      // Timeout after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (verificationStatus === 'pending') {
          setVeriffError('Verification timed out. Please try again.');
        }
      }, 600000);
    }
  };

  // DAILY.CO INTEGRATION
  const callObjectRef = useRef(null);
  const [roomInfo, setRoomInfo] = useState(null);
  const [isDailyRecording, setIsDailyRecording] = useState(false);
  const [dailyJoined, setDailyJoined] = useState(false);

  const getDeepgramLanguageCode = (languageName) => {
    const languageMap = {
      'German': 'de',
      'French': 'fr',
      'Italian': 'it',
      'Turkish': 'tr',
    };
    return languageMap[languageName] || 'en';
  };

  const getDeviceType = () => {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  };

  const isSafari = () => {
    const ua = navigator.userAgent;
    return ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Chromium');
  };

  const isIPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints && navigator.maxTouchPoints > 2;

  // Function to enumerate available media devices
  const getAvailableDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      const audioDevices = devices.filter(device => device.kind === 'audioinput');
      
      setAvailableVideoDevices(videoDevices);
      setAvailableAudioDevices(audioDevices);
      
      // Set default devices if none selected
      if (!selectedVideoDeviceId && videoDevices.length > 0) {
        setSelectedVideoDeviceId(videoDevices[0].deviceId);
      }
      if (!selectedAudioDeviceId && audioDevices.length > 0) {
        setSelectedAudioDeviceId(audioDevices[0].deviceId);
      }
    } catch (error) {
      console.error('Error enumerating devices:', error);
    }
  };

  // Function to check and request audio/video permissions
  const checkPermissions = async () => {
    setPermissionError('');
    try {
      // Request permissions first to get accurate device list (fixes Safari issue)
      const tempStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      tempStream.getTracks().forEach(track => track.stop());
      
      // Now get available devices with proper labels
      await getAvailableDevices();
      
      // Request both audio and video permissions with specific devices if selected
      const constraints = {
        video: selectedVideoDeviceId ? { deviceId: { exact: selectedVideoDeviceId } } : true,
        audio: selectedAudioDeviceId ? { deviceId: { exact: selectedAudioDeviceId } } : true,
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Check if both audio and video tracks are available
      const videoTracks = stream.getVideoTracks();
      const audioTracks = stream.getAudioTracks();

      const hasVideo = videoTracks.length > 0 && videoTracks[0].enabled;
      const hasAudio = audioTracks.length > 0 && audioTracks[0].enabled;

      setVideoPermissionGranted(hasVideo);
      setAudioPermissionGranted(hasAudio);

      if (hasVideo && hasAudio) {
        setPreviewStream(stream);
        // Set the stream to the preview video element
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        // Start audio analysis for level visualization
        if (hasAudio) {
          startAudioAnalysis(stream).catch(err => console.error('Failed to start audio analysis:', err));
        }
      } else {
        // Stop the stream if not both permissions are granted
        stream.getTracks().forEach(track => track.stop());
        setPreviewStream(null);
        
        if (!hasVideo && !hasAudio) {
          setPermissionError('Both camera and microphone access are required to conduct the interview.');
        } else if (!hasVideo) {
          setPermissionError('Camera access is required to conduct the interview.');
        } else if (!hasAudio) {
          setPermissionError('Microphone access is required to conduct the interview.');
        }
      }

      return hasVideo && hasAudio;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      setVideoPermissionGranted(false);
      setAudioPermissionGranted(false);
      setPreviewStream(null);
      
      if (error.name === 'NotAllowedError') {
        setPermissionError('Camera and microphone access denied. Please allow access and refresh the page.');
      } else if (error.name === 'NotFoundError') {
        setPermissionError('No camera or microphone found. Please ensure your devices are connected.');
      } else {
        setPermissionError('Unable to access camera or microphone. Please check your device settings.');
      }
      
      return false;
    }
  };

  // Function to handle device selection changes
  const handleDeviceChange = async (deviceId, deviceType) => {
    if (deviceType === 'video') {
      setSelectedVideoDeviceId(deviceId);
    } else if (deviceType === 'audio') {
      setSelectedAudioDeviceId(deviceId);
    }
    
    // If permissions are already granted, restart the stream with new device
    if (videoPermissionGranted && audioPermissionGranted) {
      try {
        // Stop current stream and audio analysis
        if (previewStream) {
          previewStream.getTracks().forEach(track => track.stop());
        }
        stopAudioAnalysis();
        
        // Request new stream with updated constraints
        const constraints = {
          video: deviceType === 'video' ? { deviceId: { exact: deviceId } } : 
                 selectedVideoDeviceId ? { deviceId: { exact: selectedVideoDeviceId } } : true,
          audio: deviceType === 'audio' ? { deviceId: { exact: deviceId } } :
                 selectedAudioDeviceId ? { deviceId: { exact: selectedAudioDeviceId } } : true,
        };
        
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        setPreviewStream(stream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        
        // Restart audio analysis for the new stream
        startAudioAnalysis(stream).catch(err => console.error('Failed to restart audio analysis:', err));
      } catch (error) {
        console.error('Error switching device:', error);
      }
    }
  };

  // Function to stop preview stream
  const stopPreviewStream = () => {
    if (previewStream) {
      previewStream.getTracks().forEach(track => track.stop());
      setPreviewStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    stopAudioAnalysis();
  };

  // Audio analysis functions
  const startAudioAnalysis = async (stream) => {
    console.log('🔊 Starting audio analysis...');
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      console.log('Audio context state:', audioContext.state);
      
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
        console.log('Audio context resumed, new state:', audioContext.state);
      }
      
      // Check if stream has audio tracks
      const audioTracks = stream.getAudioTracks();
      console.log('Audio tracks found:', audioTracks.length);
      if (audioTracks.length > 0) {
        console.log('Audio track enabled:', audioTracks[0].enabled);
        console.log('Audio track readyState:', audioTracks[0].readyState);
        console.log('Audio track settings:', audioTracks[0].getSettings());
        
        // Test if microphone is actually receiving input
        const testAnalyser = audioContext.createAnalyser();
        const testSource = audioContext.createMediaStreamSource(stream);
        testSource.connect(testAnalyser);
        testAnalyser.fftSize = 32;
        const testData = new Uint8Array(testAnalyser.frequencyBinCount);
        
        setTimeout(() => {
          testAnalyser.getByteFrequencyData(testData);
          const testSum = Array.from(testData).reduce((a, b) => a + b, 0);
          console.log('🔍 Microphone test - Raw frequency data:', Array.from(testData).slice(0, 8));
          console.log('🔍 Microphone test - Total sum:', testSum);
          
          // Disconnect test
          testSource.disconnect();
        }, 100);
      }
      
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.1;
      const dataArray = new Uint8Array(analyser.fftSize);

      microphone.connect(analyser);
      audioAnalyserRef.current = { analyser, dataArray, audioContext };

      const updateAudioLevel = () => {
        if (audioAnalyserRef.current) {
          const { analyser, dataArray } = audioAnalyserRef.current;
          analyser.getByteTimeDomainData(dataArray);

          // Calculate RMS from time-domain data
          let sumSquares = 0;
          for (let i = 0; i < dataArray.length; i++) {
            const centered = (dataArray[i] - 128) / 128;
            sumSquares += centered * centered;
          }
          const rms = Math.sqrt(sumSquares / dataArray.length);
          const normalizedLevel = Math.min(Math.max(rms * 4, 0), 1);

          setAudioLevel(normalizedLevel);
          animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
        }
      };

      updateAudioLevel();
      console.log('✅ Audio analysis setup complete');
    } catch (error) {
      console.error('❌ Error setting up audio analysis:', error);
    }
  };

  const stopAudioAnalysis = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioAnalyserRef.current?.audioContext) {
      try { audioAnalyserRef.current.audioContext.close(); } catch {}
    }
    audioAnalyserRef.current = null;
    setAudioLevel(0);
  };

  const getRelativeTimestamp = () => {
    if (!interviewStartTimeRef.current) return "0:00";
    const elapsedSeconds = Math.floor((Date.now() - interviewStartTimeRef.current) / 1000);
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Initialize Nora audio for TTS
  async function initNoraAudioGraph() {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      try { await audioContextRef.current.resume(); } catch {}
    }

    if (!audioDestinationRef.current || audioDestinationRef.current.context !== audioContextRef.current) {
      audioDestinationRef.current = audioContextRef.current.createMediaStreamDestination();
    }

    if (!noraAudioRef.current) {
      const el = new Audio();
      el.setAttribute('playsinline', 'true');
      el.muted = false;
      noraAudioRef.current = el;
    }

    if (!noraSourceRef.current || noraSourceRef.current.context !== audioContextRef.current) {
      try {
        try { noraSourceRef.current?.disconnect(); } catch {}
        noraSourceRef.current = audioContextRef.current.createMediaElementSource(noraAudioRef.current);
        noraSourceRef.current.connect(audioContextRef.current.destination);
        if (!isIOS && audioDestinationRef.current) {
          noraSourceRef.current.connect(audioDestinationRef.current);
        }
      } catch (e) {
        try { noraSourceRef.current?.disconnect(); } catch {}
        noraSourceRef.current = audioContextRef.current.createMediaElementSource(noraAudioRef.current);
        noraSourceRef.current.connect(audioContextRef.current.destination);
        if (!isIOS && audioDestinationRef.current) {
          noraSourceRef.current.connect(audioDestinationRef.current);
        }
      }
    }
  }

  async function playNoraTTSBlob(blob) {
    if (isIOS && webAudioFailed) {
      const url = URL.createObjectURL(blob);
      const el = new Audio(url);
      el.setAttribute('playsinline', 'true');
      el.volume = 1.0;
      setIsNoraSpeaking(true);
      try { await el.play(); } catch {}
      el.onended = el.onerror = () => {
        setIsNoraSpeaking(false);
        noraFinishedSpeakingTimeRef.current = Date.now(); 
        URL.revokeObjectURL(url);
      };
      return;
    }

    try {
      await initNoraAudioGraph();

      const url = URL.createObjectURL(blob);
      const el = noraAudioRef.current;
      
      try { el.pause(); } catch {}
      el.currentTime = 0;
      el.volume = 1.0; 
      el.src = url;
      
      setIsNoraSpeaking(true);
      
      try {
        await el.play();
      } catch (e) {
        try { await audioContextRef.current?.resume?.(); } catch {}
        try {
          await el.play();
        } catch {
          if (isIOS) {
            setWebAudioFailed(true);
            const direct = new Audio(url);
            direct.setAttribute('playsinline', 'true');
            try { await direct.play(); } catch {}
            direct.onended = direct.onerror = () => {
              setIsNoraSpeaking(false);
              noraFinishedSpeakingTimeRef.current = Date.now();
              URL.revokeObjectURL(url);
            };
            return;
          }
        }
      }
      el.onended = el.onerror = () => {
        setIsNoraSpeaking(false);
        noraFinishedSpeakingTimeRef.current = Date.now();
        URL.revokeObjectURL(url);
      };
    } catch (routeErr) {
      if (isIOS) {
        setWebAudioFailed(true);
        const url = URL.createObjectURL(blob);
        const el = new Audio(url);
        el.setAttribute('playsinline', 'true');
        el.volume = 1.0;
        setIsNoraSpeaking(true);
        try { await el.play(); } catch {}
        el.onended = el.onerror = () => {
          setIsNoraSpeaking(false);
          noraFinishedSpeakingTimeRef.current = Date.now();
          URL.revokeObjectURL(url);
        };
      }
    }
  }

  // Cheating detection
  useEffect(() => {
    if (!started) return;

    const addCheatingIncident = (type, description) => {
      const incident = {
        timestamp: getRelativeTimestamp(),
        type: type,
        description: description
      };
      
      setCheatingIncidents(prev => [...prev, incident]);
      cheatingIncidentsRef.current.push(incident);
      
      console.log(`🚨 INCIDENT LOGGED: ${incident.timestamp} - ${description}`);
    };

    let isPageVisible = true;
    let visibilityLostTime = null;
    let lastIncidentTime = 0;
    let keystrokeTimestamps = [];
    const BURST_THRESHOLD = 8;
    const BURST_WINDOW = 10000;
    const MIN_SUSPICIOUS_TIME = 3000;

    const incrementCheatingCounter = (reason, timeAway = null) => {
      const now = Date.now();
      if (now - lastIncidentTime < 1000) return;
      
      setCheatingCounter(prev => {
        const newCount = prev + 1;
        lastIncidentTime = now;
        
        let description = reason;
        if (reason.includes('Tab switch') || reason.includes('Window switch')) {
          const seconds = (timeAway / 1000).toFixed(1);
          description = `Tab switch detected (away for ${seconds}s)`;
          addCheatingIncident('tab_switch', description);
        } else if (reason.includes('Keyboard shortcut')) {
          addCheatingIncident('keyboard_shortcut', description);
        } else if (reason.includes('Typing burst')) {
          addCheatingIncident('typing_burst', description);
        }
        
        return newCount;
      });
    };

    const handleKeyPress = (event) => {
      const now = Date.now();
      const isTypingKey = /^[a-zA-Z0-9\s.,!?'"()-]$/.test(event.key);
      
      if (isTypingKey) {
        keystrokeTimestamps.push(now);
        keystrokeTimestamps = keystrokeTimestamps.filter(timestamp => 
          now - timestamp <= BURST_WINDOW
        );
        
        if (keystrokeTimestamps.length >= BURST_THRESHOLD) {
          const burstDuration = (now - keystrokeTimestamps[0]) / 1000;
          incrementCheatingCounter(`Typing burst: ${keystrokeTimestamps.length} keystrokes in ${burstDuration.toFixed(1)}s`);
          keystrokeTimestamps = [];
        }
      }
    };

    const handleKeyDown = (event) => {
      const { key, metaKey, ctrlKey, altKey } = event;
      
      if (metaKey || ctrlKey) {
        let suspiciousShortcut = null;
        
        switch(key.toLowerCase()) {
          case 'h':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+H (Hide window/History)`;
            break;
          case 'b':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+B (Bookmarks/Bold - suspicious)`;
            break;
          case 'c':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+C (Copy - suspicious)`;
            break;
          case 'v':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+V (Paste - suspicious)`;
            break;
          case '/':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+/ (Help/Search shortcuts)`;
            break;
          case 'enter':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+Enter (AI tool submit - highly suspicious)`;
            break;
          case 'arrowup':
          case 'arrowdown':
          case 'arrowleft':
          case 'arrowright':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+Arrow (Navigation shortcuts)`;
            break;
          case 'tab':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+Tab (App switching)`;
            break;
          case '`':
            suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+\` (App cycling)`;
            break;
          case 'shift':
            if (metaKey || ctrlKey) {
              suspiciousShortcut = `${metaKey ? 'Cmd' : 'Ctrl'}+Shift (Advanced shortcuts)`;
            }
            break;
          default:
            break;
        }
        
        if (altKey && key.toLowerCase() === 'tab') {
          suspiciousShortcut = 'Alt+Tab (Window switching)';
        }
        
        if (suspiciousShortcut) {
          incrementCheatingCounter(`Keyboard shortcut: ${suspiciousShortcut}`);
          if (key.toLowerCase() === 'h' || key.toLowerCase() === 'b') {
            event.preventDefault();
          }
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden && isPageVisible) {
        isPageVisible = false;
        visibilityLostTime = Date.now();
      } else if (!document.hidden && !isPageVisible) {
        isPageVisible = true;
        if (visibilityLostTime) {
          const timeAway = Date.now() - visibilityLostTime;
          if (timeAway >= MIN_SUSPICIOUS_TIME) {
            incrementCheatingCounter('Tab switch detected', timeAway);
          }
          visibilityLostTime = null;
        }
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('keypress', handleKeyPress);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [started]);

  useEffect(() => {
    if (cheatingCounter > 0) {
      console.log(`📊 Total cheating incidents: ${cheatingCounter}`);
    }
  }, [cheatingCounter]);

  // DAILY.CO FUNCTIONS
  const getDailyMixedAudioStream = async () => {
    try {
      await initNoraAudioGraph();

      if (!dailyAudioInputStreamRef.current) {
        const audioConstraints = selectedAudioDeviceId
          ? { deviceId: { exact: selectedAudioDeviceId } }
          : true;
        dailyAudioInputStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false
        });
      }

      if (!dailyMicSourceRef.current && audioContextRef.current && audioDestinationRef.current) {
        dailyMicSourceRef.current = audioContextRef.current.createMediaStreamSource(
          dailyAudioInputStreamRef.current
        );
        dailyMicSourceRef.current.connect(audioDestinationRef.current);
      }

      return audioDestinationRef.current?.stream || null;
    } catch (error) {
      console.error('Failed to prepare Daily mixed audio stream:', error);
      return null;
    }
  };

  const createDailyRoom = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/api/Daily/createDailyRoom`,
        { 
          candidateToInterviewId,
          timestamp: Date.now() // Add timestamp to ensure uniqueness
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to create Daily room:', error);
      
      // If room already exists, try to get existing room info or use the existing room
      if (error.response?.data?.error === 'invalid-request-error' && 
          error.response?.data?.info?.includes('already exists')) {
        console.log('🔄 Room already exists, attempting to get existing room info...');
        
        try {
          // Try to get existing room info instead of creating new one
          const getRoomResponse = await axios.get(
            `${process.env.REACT_APP_API_ENDPOINT}/api/Daily/getRoomInfo/${candidateToInterviewId}`,
            { headers: { 'Content-Type': 'application/json' } }
          );
          
          console.log('✅ Using existing room:', getRoomResponse.data);
          return getRoomResponse.data;
        } catch (getRoomError) {
          console.warn('Could not get existing room info:', getRoomError);
          
          // Fallback: construct room info from the error message
          const roomName = error.response?.data?.info?.match(/room named (.+) already exists/)?.[1];
          if (roomName) {
            console.log('📝 Constructing room info from error message...');
            return {
              roomName,
              roomUrl: `https://internguys.daily.co/${roomName}`,
              // Add other expected properties if needed
            };
          }
        }
      }
      
      Sentry.captureException(error, {
        extra: { message: "Failed to create Daily room", candidateToInterviewId }
      });
      throw error;
    }
  };

  const joinDailyRoom = async (roomUrl) => {
    try {
      // Only create call object if it doesn't exist
      if (!callObjectRef.current) {
        callObjectRef.current = DailyIframe.createCallObject();
      }

      // Only set up event listeners if they haven't been set up yet
      if (!dailyListenersAttached.current) {
        callObjectRef.current.on('track-started', (event) => {
          if (event.participant.local && event.track.kind === 'video') {
            if (videoRef.current) {
              videoRef.current.srcObject = new MediaStream([event.track]);
            }
          }
        });

        callObjectRef.current.on('joined-meeting', async () => {
          setDailyJoined(true);
          console.log('✅ Joined Daily room');
          
          // Safari fix: Ensure microphone is explicitly enabled after joining
          if (isSafari()) {
            try {
              console.log('🍎 Safari detected - applying audio fix...');
              await callObjectRef.current.setLocalAudio(true);
              
              // Additional Safari workaround: Get fresh audio stream
              const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
              await callObjectRef.current.setInputDevicesAsync({
                audioDeviceId: stream.getAudioTracks()[0]?.getSettings()?.deviceId || 'default'
              });
              stream.getTracks().forEach(track => track.stop()); // Clean up test stream
              
              console.log('✅ Safari audio fix applied');
            } catch (safariError) {
              console.warn('Safari audio fix failed:', safariError);
            }
          }
        });

        callObjectRef.current.on('recording-started', () => {
          setIsDailyRecording(true);
          console.log('✅ Daily recording started');
          
          // Additional Safari check after recording starts
          if (isSafari()) {
            setTimeout(async () => {
              try {
                await callObjectRef.current.setLocalAudio(true);
                console.log('🔊 Safari: Re-enabled audio after recording start');
              } catch (err) {
                console.warn('Safari audio re-enable failed:', err);
              }
            }, 1000);
          }
        });

        callObjectRef.current.on('recording-stopped', () => {
          setIsDailyRecording(false);
          console.log('⏹️ Daily recording stopped');
        });

        callObjectRef.current.on('error', (error) => {
          console.error('Daily error:', error);
        });

        dailyListenersAttached.current = true;
      }

      // Only join if not already joined
      if (!dailyJoined) {
        await callObjectRef.current.join({ 
          url: roomUrl,
          // Safari-specific join options
          userName: candidateInfo?.candidateToInterview?.name || 'Candidate',
          audioSource: true,
          videoSource: true
        });

        // After joining, switch to mixed audio so Nora is in the recording
        const mixedAudioStream = await getDailyMixedAudioStream();
        const mixedAudioTrack = mixedAudioStream?.getAudioTracks?.()[0];
        if (mixedAudioTrack) {
          try {
            await callObjectRef.current.setInputDevicesAsync({ audioSource: mixedAudioTrack });
          } catch (setAudioErr) {
            console.warn('Could not set mixed audio source for Daily:', setAudioErr);
          }
        }
      }
    } catch (error) {
      console.error('Failed to join Daily room:', error);
      Sentry.captureException(error, {
        extra: { message: "Failed to join Daily room", roomUrl }
      });
      throw error;
    }
  };

  const startDailyRecording = async () => {
    if (callObjectRef.current) {
      try {
        if (isSafari()) {
          console.log('🍎 Safari: Ensuring audio is enabled before recording...');
          await callObjectRef.current.setLocalAudio(true);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        await callObjectRef.current.startRecording();
        console.log('🔴 Started Daily recording');
      } catch (error) {
        console.error('Failed to start Daily recording:', error);
        Sentry.captureException(error, {
          extra: { candidateToInterviewId, roomName: roomInfo?.roomName }
        });
      }
    } else {
      Sentry.captureMessage('startDailyRecording: no call object', { extra: { candidateToInterviewId } });
    }
  };

  const stopDailyRecording = async () => {
    if (callObjectRef.current) {
      try {
        await callObjectRef.current.stopRecording();
        console.log('⏹️ Stopped Daily recording');
      } catch (error) {
        console.error('Failed to stop Daily recording:', error);
      }
    }
  };

  const leaveDailyRoom = async () => {
    if (callObjectRef.current) {
      try {
        await callObjectRef.current.leave();
        await callObjectRef.current.destroy();
        setDailyJoined(false);
        dailyAudioListenerAttached.current = false;
        console.log('👋 Left Daily room');
        
        // Optional: Delete room from Daily.co to prevent "already exists" errors
        if (roomInfo?.roomName) {
          try {
            await axios.delete(
              `${process.env.REACT_APP_API_ENDPOINT}/api/Daily/deleteRoom/${roomInfo.roomName}`,
              { headers: { 'Content-Type': 'application/json' } }
            );
            console.log('🗑️ Cleaned up Daily room');
          } catch (deleteError) {
            console.warn('Could not delete room:', deleteError);
          }
        }

        try { dailyMicSourceRef.current?.disconnect(); } catch {}
        dailyMicSourceRef.current = null;
        if (dailyAudioInputStreamRef.current) {
          dailyAudioInputStreamRef.current.getTracks().forEach(track => track.stop());
          dailyAudioInputStreamRef.current = null;
        }
      } catch (error) {
        console.error('Failed to leave Daily room:', error);
      }
    }
  };

  const getDailyRecording = async (roomName) => {
    try {
      let attempts = 0;
      const maxAttempts = 60;
      console.log(`🎬 Starting recording fetch for room: ${roomName}`);

      while (attempts < maxAttempts) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_ENDPOINT}/api/Daily/getDailyRecording`,
          { roomName },
          { headers: { 'Content-Type': 'application/json' } }
        );

        const data = response.data;
        
        console.log(`📊 Recording status response:`, {
          status: data.status,
          hasDownloadUrl: !!data.downloadUrl,
          attempt: attempts + 1,
          maxAttempts,
          roomName
        });

        if (data.status === 'finished' && data.downloadUrl) {
          console.log('✅ Recording ready:', data.downloadUrl);
          return data.downloadUrl;
        }
        
        if (data.status === 'failed' || data.status === 'error') {
          throw new Error(`Recording processing failed with status: ${data.status}`);
        }

        console.log(`⏳ Recording processing... (attempt ${attempts + 1}/${maxAttempts})`);
        await new Promise(resolve => setTimeout(resolve, 5000));
        attempts++;
      }

      throw new Error('Recording processing timeout');
    } catch (error) {
      console.error('Failed to get Daily recording:', error);
      throw error;
    }
  };

  // Fetch candidate info
  useEffect(() => {
    const fetchCandidateInfo = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_GET_INTERVIEW_STARTED_DATA, {
          params: { candidateToInterviewId },
          headers: { 'Content-Type': 'application/json' },
        });
        setCandidateInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch candidate information", error);
        Sentry.captureException(error, {
          extra: {
            message: "Failed to fetch candidate information",
            candidateToInterviewId,
            endpoint: process.env.REACT_APP_GET_INTERVIEW_STARTED_DATA,
            errorDetails: error.response?.data || error.message
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateInfo();
  }, [candidateToInterviewId]);

  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, null, window.location.href);
      alert("You cannot go back during the interview.");
    };
  
    window.history.pushState(null, null, window.location.href);
    window.addEventListener("popstate", handlePopState);
  
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "Are you sure you want to leave? Your interview progress will be lost.";
    };
  
    window.addEventListener("beforeunload", handleBeforeUnload);
  
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = "#F7F7F7";
    return () => {
      document.body.style.backgroundColor = "#203546";
      
      // Clean up audio analysis
      stopAudioAnalysis();
      
      // Clean up preview stream
      if (previewStream) {
        previewStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [previewStream]);

  useEffect(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(prevTime => prevTime + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  const getAssemblyAILanguageCode = (languageName) => {
    if (!languageName) return 'en';
    const languageMap = {
      'English': 'en',
      'Spanish': 'es',
      'Portuguese': 'pt',
    };
    return languageMap[languageName] || 'en';
  };

  const getGoogleTTSLanguageCode = (languageName) => {
    if (!languageName) return 'en-US';
    const languageMap = {
      'English': 'en-US',
      'Spanish': 'es-ES',
      'French': 'fr-FR',
      'German': 'de-DE',
      'Italian': 'it-IT',
      'Portuguese': 'pt-BR',
      'Turkish': 'tr-TR',
      'Hindi': 'hi-IN',
    };
    return languageMap[languageName] || 'en-US';
  };

  function detectLanguageForTTS(text) {
    const detectedLangISO3 = franc(text, {
      minLength: 10,
      whitelist: ['eng', 'fra', 'deu', 'ita', 'por', 'spa', 'tur', 'hin']
    });
    
    const langMap = {
      'eng': 'en-US',
      'fra': 'fr-FR',
      'deu': 'de-DE',
      'ita': 'it-IT',
      'por': 'pt-BR',
      'spa': 'es-ES',
      'tur': 'tr-TR',
      'hin': 'hi-IN',
    };
    
    return langMap[detectedLangISO3] || 'en-US';
  }

  const startRecording = async () => {
    setRecord(true);
    setHasError(false);
    accumulatedTranscriptRef.current = '';

    if (noraFinishedSpeakingTimeRef.current) {
      const latency = (Date.now() - noraFinishedSpeakingTimeRef.current) / 1000;
      responseLatenciesRef.current.push(latency);
      console.log(`⏱️ Response latency: ${latency.toFixed(1)}s`);
    }
    
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });

      // Kick off local audio level visualization using the same stream
      try {
        stopAudioAnalysis();
        await startAudioAnalysis(stream);
      } catch (levelErr) {
        console.warn('Audio level analysis failed to start:', levelErr);
      }
      const audioContext = new AudioContext({ sampleRate: 16000 });
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      source.connect(processor);
      processor.connect(audioContext.destination);

      const interviewLanguage = candidateInfo?.companyInterview?.interviewLanguage;
      const useDeepgram = ['German', 'French', 'Italian', 'Turkish'].includes(interviewLanguage);

      let ws;

      if (useDeepgram) {
        // Deepgram for German, French, Italian, Turkish
        const deepgramLangCode = getDeepgramLanguageCode(interviewLanguage);
        const wsUrl = `wss://api.deepgram.com/v1/listen?encoding=linear16&sample_rate=16000&language=${deepgramLangCode}`;
        
        ws = new WebSocket(wsUrl, ['token', '9a6d21be1f08cf4be5686afb305e886a6482a1f8']);
        ws.binaryType = 'arraybuffer';

        ws.onopen = () => {
          console.log('✅ Deepgram connected for', interviewLanguage);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const transcript = data.channel?.alternatives?.[0]?.transcript;
            
            if (transcript && realtimeTranscriberRef.current) {
              realtimeTranscriberRef.current.lastPartialTranscript = transcript;
              
              if (data.is_final) {
                accumulatedTranscriptRef.current += (accumulatedTranscriptRef.current ? ' ' : '') + transcript;
              }
            }
          } catch (error) {
            console.error('Error parsing Deepgram message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('❌ Deepgram WebSocket error:', error);
          setHasError(true);
        };

      } else {
        // AssemblyAI for English, Spanish, Portuguese, etc.
        const params = {
          token: "d1ed33af12ac4d478785ac40bb4599a7",
          sample_rate: audioContext.sampleRate,
          encoding: 'pcm_s16le',
          format_turns: true
        };

        if (!candidateInfo?.companyInterview?.bilingualLanguage) {
          params.language = getAssemblyAILanguageCode(interviewLanguage);
        }

        const queryString = new URLSearchParams(params).toString();
        const wsUrl = `wss://streaming.assemblyai.com/v3/ws?${queryString}`;
        ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        
        ws.onopen = () => {
          console.log('✅ AssemblyAI connected for', interviewLanguage);
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'Begin') {
              console.log('Session started:', data.id);
            } else if (data.type === 'Turn') {
              if (data.transcript && data.turn_is_formatted) {
                if (realtimeTranscriberRef.current) {
                  realtimeTranscriberRef.current.lastPartialTranscript = data.transcript;
                  
                  if (data.end_of_turn) {
                    accumulatedTranscriptRef.current += (accumulatedTranscriptRef.current ? ' ' : '') + data.transcript;
                  }
                }
              }
              
              if (data.transcript && realtimeTranscriberRef.current) {
                realtimeTranscriberRef.current.lastPartialTranscript = data.transcript;
              }
            }
          } catch (error) {
            console.error('Error parsing message:', error);
          }
        };

        ws.onerror = (error) => {
          console.error('❌ AssemblyAI WebSocket error:', error);
          setHasError(true);
        };
      }

      processor.onaudioprocess = (e) => {
        if (ws.readyState === 1) {
          const inputData = e.inputBuffer.getChannelData(0);
          const pcm16 = new Int16Array(inputData.length);
          
          for (let i = 0; i < inputData.length; i++) {
            const s = Math.max(-1, Math.min(1, inputData[i]));
            pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
          }
          
          ws.send(pcm16.buffer);
        }
      };

      realtimeTranscriberRef.current = { 
        ws, 
        audioContext, 
        processor, 
        stream,
        lastPartialTranscript: '' 
      };
      
    } catch (error) {
      console.error('❌ Failed to start recording:', error);
      setHasError(true);
      setIsLoading(false);
    }
  };

  const stopRecording = async () => {
    setRecord(false);
    setIsLoading(true);
    
    const refs = realtimeTranscriberRef.current;
    if (refs) {
      try {
        await new Promise(resolve => setTimeout(resolve, 220));
        
        const interviewLanguage = candidateInfo?.companyInterview?.interviewLanguage;
        const useDeepgram = ['German', 'French', 'Italian', 'Turkish'].includes(interviewLanguage);
        
        if (refs.ws?.readyState === 1) {
          const silence = new Int16Array((refs.audioContext.sampleRate / 1000) * 100);
          refs.ws.send(silence.buffer);
        }
        
        // Only send Terminate for AssemblyAI
        if (!useDeepgram && refs.ws && refs.ws.readyState === 1) {
          refs.ws.send(JSON.stringify({ type: 'Terminate' }));
        }
                
        await new Promise(resolve => setTimeout(resolve, useDeepgram ? 500 : 2000));
        
        console.log('✅ Transcription closed');
        
        try { refs.processor.disconnect(); } catch {}
        try { await refs.audioContext.close(); } catch {}
        try { refs.stream.getTracks().forEach(t => t.stop()); } catch {}
        
        if (refs.ws && refs.ws.readyState === 1) {
          refs.ws.close();
        }
        
        let transcript = accumulatedTranscriptRef.current.trim();
        
        if (!transcript && refs.lastPartialTranscript) {
          transcript = refs.lastPartialTranscript.trim();
        }
        
        if (transcript) {
          handleSendMessage(transcript);
        } else {
          console.warn('⚠️ No transcript captured');
          setHasError(true);
          setIsLoading(false);
        }
        
      } catch (error) {
        console.error('❌ Error stopping transcription:', error);
        setHasError(true);
        setIsLoading(false);
      } finally {
        stopAudioAnalysis();
        realtimeTranscriberRef.current = null;
        accumulatedTranscriptRef.current = '';
      }
    }
  };

  const handleConfirmEnd = () => {
    setShowEndModal(false);
    handleEndInterview();
  };
  
  const handleCancelEnd = () => {
    setShowEndModal(false);
  };

  const ensureAudioReady = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
    } catch (e) {}
  };

  const handleEndInterviewClick = () => {
    if (history.some((msg) => msg.message.includes("End of the interview"))) {
      handleEndInterview(); 
    } else {
      setShowEndModal(true); 
    }
  };

  const retryRequest = async (fn, retries = 2, interval = 1000) => {
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        setHasError(false);
        return await fn();
      } catch (error) {
        if (attempt < retries - 1) {
          await new Promise(resolve => setTimeout(resolve, interval));
        } else {
          setHasError(true);
          Sentry.captureException(error, {
            extra: {
              message: "Retry function failed",
              candidateToInterviewId: candidateToInterviewId,
              retriesAttempted: retries,
              intervalBetweenRetries: interval,
              lastError: error.message,
            },
          });
          throw error;
        }
      }
    }
  };

  const onStop = (recordedBlob) => {
    // No longer needed with Daily
  };

  // MODIFIED: toggleVideo now uses Daily
  const toggleVideo = async () => {
    if (isVideoOn) {
      await leaveDailyRoom();
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } else {
      try {
        // Only create room if we don't have one yet
        let room = roomInfo;
        if (!room) {
          room = await createDailyRoom();
          setRoomInfo(room);
        }
        
        // Only join if we're not already in a call
        if (!dailyJoined && callObjectRef.current) {
          await joinDailyRoom(room.roomUrl);
          await startDailyRecording();
        } else if (!callObjectRef.current) {
          // Create call object if it doesn't exist
          await joinDailyRoom(room.roomUrl);
          await startDailyRecording();
        }
        
        console.log('✅ Daily room created and recording started');
      } catch (err) {
        console.error("Error starting Daily video:", err);
        Sentry.captureException(err, {
          extra: {
            message: "Failed to start Daily video",
            candidateToInterviewId
          }
        });
      }
    }

    setIsVideoOn(!isVideoOn);
  };

  async function speakTTS(text) {
    await ensureAudioReady();

    const languageCode = candidateInfo?.companyInterview?.bilingualLanguage
      ? detectLanguageForTTS(text)
      : getGoogleTTSLanguageCode(candidateInfo?.companyInterview?.interviewLanguage);

    try {
      const resp = await retryRequest(() =>
        axios.post(
          `${process.env.REACT_APP_API_ENDPOINT}/Chat/textToSpeech`,
          { text, languageCode },
          { responseType: "blob" },
        ),
      );
      if (resp.status === 200) {
        const blob = new Blob([resp.data], { type: "audio/wav" });
        await playNoraTTSBlob(blob);
      }
    } catch (e) {
      console.warn("TTS failed:", e);
    }
  }

  const handleSendMessage = async (transcription) => {
    setHasError(false);
    const formData = new FormData();

    formData.append('position', candidateInfo?.companyInterview?.position);
    formData.append('jobDescription', candidateInfo?.companyInterview?.jobDescription);
    formData.append('UserMessage', transcription);
    formData.append('CompanyName', candidateInfo?.company?.companyName);
    formData.append('InterviewScript', candidateInfo?.companyInterview?.interviewScript);
    formData.append('InterviewLanguage', candidateInfo?.companyInterview?.interviewLanguage || 'English');
    formData.append('BilingualLanguage', candidateInfo?.companyInterview?.bilingualLanguage ? 1 : 0);
    formData.append('CandidateName', candidateInfo?.candidateToInterview?.name || '');

    const updatedHistory = [...history, { role: 'User', message: transcription }];
    
    setHistory(updatedHistory);
    formData.append('History', JSON.stringify(updatedHistory));

    try {
      const response = await retryRequest(() => axios.post(`${process.env.REACT_APP_API_ENDPOINT}/Chat/nora-candidate-interview-main`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }));
      const { response: aiResponse } = response.data;

      if (!aiResponse.toLowerCase().includes("end of the interview") && interviewStartTimeRef.current) {
        const elapsedSeconds = (Date.now() - interviewStartTimeRef.current) / 1000;
        questionTimestampsRef.current.push({
          questionNumber: questionTimestampsRef.current.length + 1,
          timestampSeconds: elapsedSeconds,
          question: aiResponse
        });
        console.log(`📍 Question ${questionTimestampsRef.current.length} at ${elapsedSeconds.toFixed(1)}s`);
      }

      if (aiResponse.toLowerCase().includes("end of the interview")) {
        const completeHistory = [...updatedHistory, { role: "Assistant", message: aiResponse }];
        
        const closingLines = [
          "Thanks for taking the time to chat with me today.",
          "We'll be in touch soon.",
        ];

        for (const line of closingLines) {
          completeHistory.push({ role: "Assistant", message: line });
          setHistory((h) => [...h, { role: "Assistant", message: line }]);
          await new Promise((r) => setTimeout(r, 100)); 
          await speakTTS(line);
          await new Promise((r) => setTimeout(r, 300));
        }

        setInterviewDone(true);
        await handleEndInterview(completeHistory);
        return;
      }

      setAiResponse(aiResponse);

      let languageCode;
      if (candidateInfo?.companyInterview?.bilingualLanguage) {
        languageCode = detectLanguageForTTS(aiResponse);
      } else {
        languageCode = getGoogleTTSLanguageCode(candidateInfo?.companyInterview?.interviewLanguage);
      }

      const audioResponse = await retryRequest(() => axios.post(`${process.env.REACT_APP_API_ENDPOINT}/Chat/textToSpeech`, {
        text: aiResponse,
        languageCode: languageCode
      }, {
        responseType: 'blob'
      }));

      if (audioResponse.status === 200) {
        const audioBlob = new Blob([audioResponse.data], { type: 'audio/wav' });
        await playNoraTTSBlob(audioBlob);
      } else {
        setHasError(true);
        console.error('Failed to convert text to speech');
      }

      setHistory(prevHistory => [
        ...prevHistory,
        { role: 'Assistant', message: aiResponse },
      ]);

    } catch (error) {
      setHasError(true);
      console.error('Error sending message:', error);

      Sentry.captureException(error, {
        extra: {
          transcription,
          candidateInfo: {
            position: candidateInfo?.companyInterview?.position,
            jobDescription: candidateInfo?.companyInterview?.jobDescription,
            companyName: candidateInfo?.company?.companyName,
            candidateToInterviewId: candidateToInterviewId,
          },
        },
      });
    }
    setIsLoading(false);
  };

  async function sendNotifications(candidateToInterviewId) {
    try {
      const form = new FormData();
      form.append("candidateToInterviewId", candidateToInterviewId);
  
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/Chat/sendInterviewNotifications`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
  
      console.log("sent notifications:", data.Message);
    } catch (err) {
      console.error("failed to send notifications:", err);
    }
  }

  const handleEndInterview = async (finalHistory = null) => {
    setFeedbackLoading(true);
    
    try {
      // Stop Daily recording first
      if (isDailyRecording) {
        await stopDailyRecording();
      }

      // Leave Daily room
      await leaveDailyRoom();

      // Generate feedback (this can happen while recording processes)
      const historyToUse = finalHistory || history;
      
      const feedbackPromise = retryRequest(() => 
        axios.post(`${process.env.REACT_APP_API_ENDPOINT}/Chat/generateCandidateFeedback`, {
          Position: candidateInfo?.companyInterview?.position,
          JobDescription: candidateInfo?.companyInterview?.jobDescription,
          History: JSON.stringify(historyToUse),
          CompanyName: candidateInfo?.company?.companyName,
          CandidateToInterviewId: candidateToInterviewId,
          CheatingCount: cheatingCounter,
          IncidentLog: JSON.stringify(cheatingIncidentsRef.current),
          QuestionTimestamps: JSON.stringify(questionTimestampsRef.current),
          ScoringCriteria: candidateInfo?.companyInterview?.scoringCriteria || '', 
        }, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Accept: 'text/plain',
          },
        })
      );

      // Get Daily recording URL (now we wait for it)
      const recordingPromise = roomInfo 
        ? getDailyRecording(roomInfo.roomName).catch(err => {
            console.error('Failed to get recording:', err);
            return null; // Don't fail the whole thing if recording fails
          })
        : Promise.resolve(null);

      // Wait for both to complete
      const [feedbackResult, recordingUrl] = await Promise.all([feedbackPromise, recordingPromise]);

      // Add this (fire and forget - don't await):
      if (recordingUrl) {
          axios.post(`${process.env.REACT_APP_API_ENDPOINT}/Chat/analyzeHesitation`, {
              recordingUrl,
              candidateToInterviewId,
              responseLatencies: responseLatenciesRef.current
          }).catch(err => console.error('Hesitation analysis failed:', err));
      }

      if (recordingUrl) {
        console.log('✅ Recording URL received:', recordingUrl);
      }

      await sendNotifications(candidateToInterviewId);

      setFeedbackLoading(false);
      setFinished(true);
      setStarted(false);

    } catch (error) {
      console.error('Error ending interview:', error);
      setFeedbackLoading(false);
      setFinished(true);
      setStarted(false);
    }
  };
  
  const startInterview = async () => {
    const deviceType = getDeviceType();

    if (deviceType === 'mobile' || deviceType === 'tablet' || isIPad || isMobile) {
      setShowMobileWarningModal(true);
    }

    // Check if already verified (e.g., page refresh after verification)
    if (candidateInfo?.companyInterview?.verificationMode) {
      // Check if already verified (e.g., page refresh after verification)
      const currentStatus = await pollVerificationStatus();

      if (currentStatus === 'approved') {
        // Already verified, go straight to audio/video check
        setTesting(true);
        try {
          await checkPermissions();
          if (!callObjectRef.current) {
            callObjectRef.current = DailyIframe.createCallObject();
            dailyListenersAttached.current = false;
          }
        } catch (error) {
          console.error('Error checking permissions on start:', error);
        }
      } else {
        // Need verification first
        await startVerification();
      }
    } else {
      // No verification needed, go straight to audio/video check
      setTesting(true);
      try {
        await checkPermissions();
        if (!callObjectRef.current) {
          callObjectRef.current = DailyIframe.createCallObject();
          dailyListenersAttached.current = false;
        }
      } catch (error) {
        console.error('Error checking permissions on start:', error);
      }
    }
  };

  const proceedToActualInterview = async () => {
    // Check if both audio and video permissions are granted
    if (!audioPermissionGranted || !videoPermissionGranted) {
      setPermissionError('Both camera and microphone access are required to start the interview.');
      return;
    }

    // Stop the preview stream before starting the interview
    stopPreviewStream();

    setTesting(false);
    setStarted(true);
    setIsLoading(true);

    // Set interview start time for relative timestamps
    interviewStartTimeRef.current = Date.now();

    await ensureAudioReady();
    
    // Safari-specific audio context initialization
    if (isSafari()) {
      try {
        console.log('🍎 Safari: Initializing audio context for recording...');
        const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        tempStream.getTracks().forEach(track => track.stop());
        console.log('✅ Safari: Audio context initialized');
      } catch (safariAudioError) {
        console.warn('Safari audio context initialization failed:', safariAudioError);
      }
    }
    
    try {
      // Automatically turn on the video when starting the interview if it is off
      if (!isVideoOn) {
        await toggleVideo();
      }
      
      handleSendMessage("first");
    } catch (error) {
      console.error('Failed to start interview:', error);
      if (!isVideoOn) {
        await toggleVideo();
      }
      handleSendMessage("first");
    }
  };

  // Helper variables for permissions
  const permissionsReady = audioPermissionGranted && videoPermissionGranted;
  const openPermissionsModal = () => setShowPermissionsModal(true);
  const closePermissionsModal = () => setShowPermissionsModal(false);

  const permissionsGuides = {
    chrome: {
      title: 'Google Chrome needs your permission to access your camera and microphone.',
      sections: [
        {
          heading: 'Method 1: Using the address bar',
          steps: [
            'Look for the camera/microphone icon in the address bar (left side).',
            'Click on the icon.',
            'Select "Always allow" for camera and microphone.',
            'Click "Done" and refresh the page.'
          ]
        },
        {
          heading: 'Method 2: Using Chrome settings',
          steps: [
            'Click the three dots menu (vertical ellipsis) in the top-right corner.',
            'Go to Settings -> Privacy and security -> Site settings.',
            'Click on "Camera" and "Microphone".',
            'Make sure they are not blocked for this site.',
            'Refresh the page.'
          ]
        }
      ],
      footer: 'After enabling permissions, you may need to refresh this page for changes to take effect.'
    },
    safari: {
      title: 'Safari needs your permission to access your camera and microphone.',
      sections: [
        {
          heading: 'For macOS',
          steps: [
            'In Safari, go to Safari menu -> Settings (or Preferences).',
            'Click on the "Websites" tab.',
            'Select "Camera" and "Microphone" from the left sidebar.',
            'Find this website in the list and set to "Allow".',
            'Close settings and refresh the page.'
          ]
        },
        {
          heading: 'For iOS (iPhone/iPad)',
          steps: [
            'Open the Settings app on your device.',
            'Scroll down and tap Safari.',
            'Tap on "Camera" and select "Allow".',
            'Tap on "Microphone" and select "Allow".',
            'Return to Safari and refresh the page.'
          ]
        }
      ],
      footer: 'After enabling permissions, you may need to refresh this page for changes to take effect.'
    }
  };

  return (
    <>
      <div>
        {started && (
          <div className="interview-title">
            <div> {candidateInfo?.companyInterview?.position} Interview </div>
          </div>
        )}
      </div>

      {!started && !finished && (
        <div className="nora-header">
          <div className="logo-container">
            <img 
              src={candidateInfo?.company?.companyLogo || NoraAILogo} 
              alt={candidateInfo?.company?.companyName || "Nora AI"} 
              style={{
                height: candidateInfo?.company?.companyLogo ? '60px' : '20px',    
                width: 'auto',   
                objectFit: 'contain',    
              }}
            />
          </div>
        </div>
      )}

      <div className={`nora-form-2 ${started && isMobile ? 'nora-form-mobile' : ''}`} 
        style={isMobile && testing ? { height: '94vh' } : {}}>
        {finished ? (
          <InterviewCompletedScreen 
            candidateToInterviewId={candidateToInterviewId}
            companyName={candidateInfo?.company?.companyName}
            position={candidateInfo?.companyInterview?.position}
          />
        ) : verifying ? (
          <div className="testing-screen">
            <div className="testing-card" style={{ maxWidth: '600px', textAlign: 'center' }}>
              <div className="instruction-container2">
                <h1 style={{ marginBottom: '10px' }}>Identity Verification</h1>
                <p style={{ marginBottom: '24px', width: '100%' }}>
                  Before we begin, we need to verify your identity. 
                  This is a quick process — just take a selfie and show your government-issued ID.
                </p>
              </div>

              {verificationStatus === 'none' && (
                <div>
                  <p style={{ color: '#666', marginBottom: '16px' }}>
                    Click the button below to start verification. It will open in a new tab.
                  </p>
                  <button 
                    onClick={startVerification}
                    className="start-button2"
                    type="button"
                  >
                    Verify My Identity
                  </button>
                </div>
              )}

              {verificationStatus === 'pending' && (
                <div style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Oval color="#00BFFF" height={40} width={40} />
                  </div>
                  <p style={{ marginTop: '16px', color: '#666' }}>
                    Waiting for verification to complete...
                  </p>
                  <p style={{ fontSize: '14px', color: '#999' }}>
                    Complete the verification in the new tab, then come back here. 
                    This page will automatically proceed once you're verified.
                  </p>
                  {veriffSessionUrl && (
                    <a 
                      href={veriffSessionUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        display: 'inline-block',
                        marginTop: '12px',
                        color: '#4D5E2F',
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                    >
                      Reopen verification page
                    </a>
                  )}
                </div>
              )}

              {verificationStatus === 'declined' && (
                <div style={{ padding: '20px' }}>
                  <img src={ErrorIcon} alt="Error" style={{ width: '40px', marginBottom: '12px' }} />
                  <p style={{ color: '#d32f2f', fontWeight: '600' }}>
                    Identity verification was not successful.
                  </p>
                  <p style={{ color: '#666', marginTop: '8px' }}>
                    Please ensure you have a valid government-issued ID and try again.
                  </p>
                  <button 
                    onClick={startVerification}
                    className="start-button2"
                    type="button"
                    style={{ marginTop: '16px' }}
                  >
                    Try Again
                  </button>
                </div>
              )}

              {verificationStatus === 'approved' && (
                <div style={{ padding: '20px' }}>
                  <div style={{ 
                    width: '50px', height: '50px', borderRadius: '50%', 
                    backgroundColor: '#4CAF50', display: 'flex', 
                    alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 12px' 
                  }}>
                    <span style={{ color: 'white', fontSize: '24px' }}>✓</span>
                  </div>
                  <p style={{ color: '#4CAF50', fontWeight: '600' }}>
                    Identity verified successfully!
                  </p>
                  <p style={{ color: '#666', marginTop: '8px' }}>
                    Proceeding to audio/video check...
                  </p>
                </div>
              )}

              {veriffError && (
                <div className="permission-error" style={{ marginTop: '16px' }}>
                  <img src={ErrorIcon} alt="Error Icon" className="error-icon" />
                  <span>{veriffError}</span>
                </div>
              )}

              <div className="testing-footer" style={{ marginTop: '24px' }}>
                <button 
                  onClick={() => { setVerifying(false); setVerificationStatus('none'); }}
                  className="testing-btn testing-btn-secondary"
                  type="button"
                >
                  Back
                </button>
              </div>
            </div>
          </div>
        ) : testing ? (
          <div className="testing-screen">
            <div className="testing-card">
              <div className="instruction-container2">
                <h1 style={{ marginBottom: '10px', textAlign: 'center' }}>Video and Audio check</h1>
                <p style={{ textAlign: 'center', width: '100%' }}>Before you start, make sure your video and audio are set up properly.</p>
              </div>

              <div className="testing-body">
                {/* VIDEO SETTINGS COLUMN */}
                <div className="testing-column">
                  <div className="section-header">
                      <img src={VideoRecorderIcon} alt="Video" className="section-icon" />
                      <span className="section-title2">Video Settings</span>
                  </div>

                  <div className="video-preview-row">
                    <div className="video-preview-card">
                      <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        className="preview-video"
                        style={{ transform: 'scaleX(-1)' }}
                      />
                    </div>

                    <div className="video-copy">
                      {permissionError ? (
                        <>
                          <h3>Your camera is currently off.</h3>
                          <p>
                            Please enable it to check your framing and lighting before the interview.
                          </p>
                        </>
                      ) : (
                        <>
                          <h3>Smile, you're on camera!</h3>
                          <p>
                            Center yourself in the frame to make sure Nora
                            can see you clearly.
                          </p>
                        </>
                      )}
                  </div>
                  
                </div>

                  <div className="field-group">
                    <label className="field-label" style={{ textAlign: 'left', display: 'block' }}>Camera</label>
                    <div className="select-wrapper">
                      <select 
                        className="field-select" 
                        value={selectedVideoDeviceId}
                        onChange={(e) => handleDeviceChange(e.target.value, 'video')}
                      >
                        {availableVideoDevices.length === 0 ? (
                          <option value="">Default camera</option>
                        ) : (
                          availableVideoDevices.map(device => (
                            <option key={device.deviceId} value={device.deviceId}>
                              {device.label || `Camera ${device.deviceId.slice(0, 8)}...`}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  {showPermissionsModal && (
                    <div className="permissions-modal-overlay" onClick={closePermissionsModal}>
                      <div className="permissions-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="permissions-modal-header">
                          <div>
                            <h2>Enable Camera & Microphone Permissions</h2>
                            <p>Follow these steps to allow access to your camera and microphone.</p>
                          </div>
                          <button type="button" className="modal-close" onClick={closePermissionsModal}>
                            &times;
                          </button>
                        </div>

                        <div className="permissions-modal-tabs">
                          {['chrome', 'safari'].map(tab => (
                            <button
                              key={tab}
                              type="button"
                              className={`permissions-tab ${permissionsBrowserTab === tab ? 'active' : ''}`}
                              onClick={() => setPermissionsBrowserTab(tab)}
                            >
                              {tab === 'chrome' ? 'Chrome' : 'Safari'}
                            </button>
                          ))}
                        </div>

                        <div className="permissions-modal-body">
                          <div className="permissions-callout">
                            <span>ℹ️</span>
                            <p>{permissionsGuides[permissionsBrowserTab].title}</p>
                          </div>

                          {permissionsGuides[permissionsBrowserTab].sections.map(section => (
                            <div key={section.heading} className="permissions-section">
                              <h3>{section.heading}</h3>
                              <ol>
                                {section.steps.map(step => (
                                  <li style={{textAlign: 'left'}} key={step}>{step}</li>
                                ))}
                              </ol>
                            </div>
                          ))}

                          <div className="permissions-callout muted">
                            <span>⚠️</span>
                            <p>{permissionsGuides[permissionsBrowserTab].footer}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* AUDIO SETTINGS COLUMN */}
                <div className="testing-column">
                  <div className="section-header">
                      <img src={AudioRecorderIcon} alt="Audio" className="section-icon" />
                      <span className="section-title2">Audio Settings</span>
                  </div>

                  <div className="field-group">
                    <label className="field-label" style={{ textAlign: 'left', display: 'block' }}>Microphone</label>
                    <div className="select-wrapper">
                      <select 
                        className="field-select" 
                        value={selectedAudioDeviceId}
                        onChange={(e) => handleDeviceChange(e.target.value, 'audio')}
                      >
                        {availableAudioDevices.length === 0 ? (
                          <option value="">{audioPermissionGranted ? "Default microphone" : "Select microphone"}</option>
                        ) : (
                          availableAudioDevices.map(device => (
                            <option key={device.deviceId} value={device.deviceId}>
                              {device.label || `Microphone ${device.deviceId.slice(0, 8)}...`}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>

                  <div className="field-group">
                    <div className="field-label-row">
                      <label className="field-label">Input level</label>
                    </div>
                    <div className="input-level-row">
                      <ReactMic
                        record={permissionsReady && testing}
                        className="input-level-bar mic-bar-visual"
                        onStop={() => {}}
                        visualSetting="frequencyBars"
                        strokeColor="#4CAF50"
                        backgroundColor="#f5f5f5"
                        echoCancellation={true}
                        autoGainControl={true}
                        noiseSuppression={true}
                        style={{
                          width: '100%',
                          height: '32px',
                          borderRadius: '12px',
                          overflow: 'hidden'
                        }}
                      />
                    </div>
                  </div>

                  {permissionError && (
                    <div className="permission-error testing-permission-error">
                      <img src={ErrorIcon} alt="Error Icon" className="error-icon" />
                      <span>{permissionError}</span>
                    </div>
                  )}

                  {!permissionsReady && (
                    <div className="tip-box tip-warning">
                      <div className="tip-warning-row">
                        <span className="tip-warning-icon">⚠️</span>
                        <p>
                          This interview requires your camera and microphone to be enabled.
                          <button type="button" className="learn-link" onClick={openPermissionsModal}>
                            Learn here
                          </button>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="testing-footer">
                <button 
                  onClick={() => setTesting(false)}
                  className="testing-btn testing-btn-secondary"
                  type="button"
                >
                  Back
                </button>
                <button 
                  onClick={proceedToActualInterview} 
                  className="start-button2"
                  type="button"
                  disabled={!permissionsReady}
                >
                  Start Interview
                </button>
              </div>
            </div>
          </div>
        ) : (!started && !finished) ? (
          <div className="instructionsImage2">
            <div className="instruction-container2">
              <h1>
                Welcome to your{" "}
                <a
                  href={candidateInfo?.companyInterview?.jobLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "underline", color: "#4D5E2F", cursor: "pointer" }}
                >
                  {candidateInfo?.companyInterview?.position}
                </a>{" "}
                Interview with Nora
              </h1>
              <p>This is the actual interview, which consists of approximately 10 questions and will last around 20 minutes.<br />Please do not exit this browser, as once you exit you will not be able to restart.</p>
            </div>
            
            <div className="demo-link">
              <p>Some tips before you begin</p>
            </div>

            <div className="instruction-cards">
              <div className="instruction-card">
                <img src={InterviewCard5} alt="Recording icon" />
                <div className="instruction-card-title">We will record the interview and by clicking <strong> 'Start Interview' </strong> you agree being recorded.</div>
              </div>
              
              <div className="instruction-card">
                <img src={InterviewCard3} alt="Quiet environment icon" />
                <div className="instruction-card-title">Ensure you are in a <strong> quiet, noise-free </strong> environment.</div>
              </div>

              <div className="instruction-card">
                <img src={InterviewCard6} alt="Chrome icon" />
                <div className="instruction-card-title">For the best experience, use a <strong> Laptop. </strong></div>
              </div>

              <div className="instruction-card1">
                <img src={InterviewCard1} alt="Interview Tutorial icon" />
                <div className="instruction-card-title">First time interviewing with Nora, our <strong> AI Interviewer? </strong></div>
                <a href="https://youtu.be/dS3S4kLVpRo" target="_blank" rel="noopener noreferrer" className="watch-tutorial-button">
                  Watch our Tutorial
                </a>
              </div>
            </div>

            <div className="interview-ready-section">
              {!isMobile && (
                <div className="ready-text">Are you ready?</div>
              )}
              <button onClick={startInterview} className="start-button2">Start Interview</button>
            </div>
          </div>
        ) : (
          <>
            {feedbackLoading && (
              <div className="loading-overlay">
                <Oval color="#00BFFF" height={80} width={80} />
                <div className="loading-text">Processing your interview (~2 minutes)! Please don't close this page </div>
              </div>
            )}
            <div className="controls-section-2">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className={`video2-feed ${isVideoOn ? '' : 'hidden'}`}
              />

              <div className="nora-timer">
                <div> 
                  <img src={VideoTimer} className="video-timer-styling" alt="Timer icon" />
                  {formatTime(time)}
                </div>
             
              </div>

              <div className="controls2">
                {(!isLoading && !isNoraSpeaking && !isInterviewDone) && (
                  <div>
                    <ReactMic
                      record={record}
                      className="mic-styling2"
                      onStop={onStop}
                      visualSetting="frequencyBars" 
                    />
                  </div>
                )}
                <div className="action-buttons">
                  <div className="video-container-2">
                    <div className="video-wrapper-2">
                      <img className={`nora-zoom ${isNoraSpeaking ? "nora-speaking" : ""}`}
                        src={NoraZoom} alt="Video of Nora" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="transcript-container">
              <div className="transcript-label">Live Transcript</div>
              <div className="transcript-divider"></div>
              <div className="message-history">
                {history.map((msg, index) => (
                  msg.message !== "first" && msg.message !== "last" && (
                    <div key={index} className={`message ${msg.role}`}>
                      {msg.message}
                    </div>
                  )
                ))}
                {hasError && (
                  <div className="error-message">
                    <img src={ErrorIcon} alt="Error Icon" className="error-icon" />
                    <span>Oops, we couldn't capture your speech. Please try recording again.</span>
                  </div>
                )}
                {isLoading && (
                  <div className="">
                    <Oval color="#00BFFF" height={20} width={20} />
                    <span>Nora is thinking...</span>
                  </div>
                )}
                <div ref={messageEndRef} />
              </div>
            </div>
          </>
        )}
      </div>

      {showEndModal && (
        <div className="endInterviewOverlay">
          <div className="endInterviewContent">
            <h2>Are you sure you want to end this interview?</h2>
            <div className="modal-buttons">
              <button onClick={handleConfirmEnd} className="modal-confirm-button">Yes, Stop</button>
              <button onClick={handleCancelEnd} className="modal-cancel-button">No, Resume</button>
            </div>
          </div>
        </div>
      )}

      {started && (
        <div className="interview-controls">
          <button 
            disabled={(!record && (isLoading || isNoraSpeaking || isInterviewDone))} 
            onClick={record ? stopRecording : startRecording}
            type="button"
            className={`startAnsweringBtn ${record ? 'stop-recording' : ''} ${(!record && (isLoading || isNoraSpeaking || isInterviewDone)) ? 'disabled' : ''}`}
          >
            {record ? 'Stop Answering' : 'Start Answering'}
          </button>
          <button onClick={handleEndInterviewClick} type="button" className="endInterviewBtn2">End Interview</button>
        </div>
      )}
    </>
  );
}

export default CandidateInterviewScreen;
